// Det fælles lyn (spor "coop"): et kort med to opgaver, én pr. rolle, og én nedtælling, som begge
// spillere ser. Din egen opgave er en knap, der sender den rigtige handling på lynets ledning; makkerens
// står som en linje, der bliver grøn, når makker har gjort sit. Står samme sted som hændelseskortet
// og bruger dets udseende, så øjet ved, hvor det skal kigge.

import type { Game } from '../game';
import type { ActionKind, Threat } from '../sim';
import { TICK_HZ } from '../sim';
import { check } from '../sim/actions';
import { DUO_BONUS, liveDuo } from '../sim/duo';
import type { DuoRole } from '../sim/duo';
import { nodeById } from '../sim/grid';
import { h, setAttr, setText, toggle } from './dom';

export interface DuoHandlers {
  act(kind: ActionKind, line: string): void;
}

interface Row {
  role: DuoRole;
  kind: ActionKind;
  el: HTMLElement;
  who: HTMLElement;
  task: HTMLElement;
  btn: HTMLButtonElement;
  state: HTMLElement;
}

const ROWS: { role: DuoRole; kind: ActionKind; who: string; task: string; done: string; btn: string }[] = [
  { role: 'drift', kind: 'reinforce_mast', who: 'Driftsvagt', task: 'Forstærk masten', done: 'Masten er forstærket', btn: 'Forstærk' },
  { role: 'plan', kind: 'upgrade_line', who: 'Planlægger', task: 'Opgradér ledningen', done: 'Ledningen er opgraderet', btn: 'Opgradér' },
];

export class DuoPanel {
  readonly el: HTMLElement;
  private time = h('span', { class: 'evcard-time' });
  private text = h('p', { class: 'evcard-text' });
  private foot = h('p', { class: 'evcard-foot' });
  private bar = h('span', { class: 'evcard-bar' });
  private rows: Row[];
  private shown = '';

  constructor(private handlers: DuoHandlers) {
    this.rows = ROWS.map((r) => {
      const who = h('span', { class: 'duo-who' }, r.who);
      const task = h('span', { class: 'duo-task' }, r.task);
      const state = h('span', { class: 'duo-state' });
      const btn = h('button', { type: 'button', class: 'evcard-opt duo-btn', 'data-role': r.role }, r.btn) as HTMLButtonElement;
      const el = h('div', { class: 'duo-row', 'data-role': r.role }, h('span', { class: 'duo-lbl' }, who, task), state, btn);
      return { role: r.role, kind: r.kind, el, who, task, btn, state };
    });
    this.el = h(
      'section',
      { class: 'evcard duo', hidden: true, role: 'group', 'aria-label': 'Fælles lyn' },
      h('div', { class: 'evcard-head' }, h('span', { class: 'evcard-title' }, 'Fælles lyn'), this.time),
      this.text,
      h('div', { class: 'duo-rows' }, ...this.rows.map((r) => r.el)),
      this.foot,
      h('span', { class: 'evcard-track', 'aria-hidden': 'true' }, this.bar),
    );
    setText(this.foot, `Begge: ledningen holder, og I får ${DUO_BONUS} kr. Kun én: den bærer det halve.`);
  }

  /** Står det fælles lyn i varslet (også bag en menu)? */
  pending(game: Game): boolean {
    return liveDuo(game.state) !== undefined;
  }

  get isOpen(): boolean {
    return !this.el.hidden && this.shown !== '';
  }

  place(maxH: number): void {
    this.el.style.maxHeight = `${Math.max(140, Math.round(maxH))}px`;
  }

  hold(on: boolean): void {
    toggle(this.el, 'held', on);
  }

  update(game: Game, covered: boolean): void {
    const s = game.state;
    const t: Threat | undefined = liveDuo(s);
    this.el.hidden = covered || !t;
    if (!t || !t.duo) {
      this.shown = '';
      return;
    }
    if (t.id !== this.shown) {
      this.shown = t.id;
      this.el.classList.remove('in');
      void this.el.offsetWidth;
      this.el.classList.add('in');
    }
    const left = Math.max(0, t.hitAt - s.clockTick);
    setText(this.time, `${Math.ceil(left / TICK_HZ)} s`);
    this.bar.style.width = `${Math.round((left / Math.max(1, t.hitAt - t.warnAt)) * 100)}%`;
    const city = nodeById(s, t.nodes?.[0] ?? '')?.name ?? 'byen';
    const both = t.duo.plan && t.duo.drift;
    setText(this.text, both ? `I nåede det begge. Ledningen til ${city} står klar til lynet.` : `Lynet sigter efter ledningen til ${city}. I skal begge nå jeres del.`);
    for (const r of this.rows) {
      const done = t.duo[r.role];
      const spec = ROWS.find((x) => x.role === r.role);
      const mine = !game.theirs(r.kind);
      const ok = !done && mine && check(s, r.kind, t.targetLine, undefined, game.player).ok;
      toggle(r.el, 'done', done);
      toggle(r.el, 'mine', mine);
      setText(r.task, done ? (spec?.done ?? '') : (spec?.task ?? ''));
      setText(r.state, done ? '✓' : mine ? '' : 'Venter');
      r.btn.hidden = done || !mine;
      r.btn.disabled = !ok;
      r.btn.onclick = () => this.handlers.act(r.kind, t.targetLine);
      setAttr(r.btn, 'aria-label', `${spec?.task ?? ''}, gratis`);
    }
  }
}
